/**
 * smc/displacement.ts
 * Displacement / impulse candle detection.
 */
import { Candle, Displacement } from '../../../../types';
import { SMCThresholds } from './types';

/**
 * Displacement: a strong, impulsive candle that:
 * - Has a large body relative to recent candles
 * - Often has above-average volume
 * - Breaks structure or creates FVG
 * These are the "signature" of institutional order flow.
 */
export function detectDisplacements(
  candles: Candle[],
  thresholds: SMCThresholds
): Displacement[] {
  if (candles.length < 10) return [];

  const displacements: Displacement[] = [];

  const lookback = Math.min(20, candles.length - 1);
  const avgBody = candles.slice(-lookback - 1, -1)
    .reduce((s, c) => s + Math.abs(c.close - c.open), 0) / lookback;

  const minBodyPct = thresholds.displacementMinBody / 100;
  const scanFrom = Math.max(1, candles.length - 30);

  for (let i = scanFrom; i < candles.length; i++) {
    const c = candles[i];
    const body = Math.abs(c.close - c.open);
    const bodyPct = body / c.close;
    const isLargeBody = bodyPct >= minBodyPct;
    const isBiggerThanAvg = avgBody > 0 && body > avgBody * 1.5;

    if (!isLargeBody && !isBiggerThanAvg) continue;

    const recentVols = candles.slice(Math.max(0, i - 10), i).map(cv => cv.volume);
    const avgVol = recentVols.length ? recentVols.reduce((s, v) => s + v, 0) / recentVols.length : c.volume;
    const volMultiple = avgVol > 0 ? c.volume / avgVol : 1;

    if (volMultiple < thresholds.displacementMinVol && !isBiggerThanAvg) continue;

    const dir = c.close > c.open ? 'bullish' : 'bearish';
    displacements.push({
      dir,
      candleIdx:      i,
      ts:             c.ts,
      size:           bodyPct * 100,
      volumeMultiple: volMultiple,
      description:    `${dir === 'bullish' ? 'Bullish' : 'Bearish'} displacement: ${bodyPct.toFixed(2)}% body candle on ${volMultiple.toFixed(1)}x volume — institutional order flow signature`,
    });
  }

  return displacements.sort((a, b) => b.candleIdx - a.candleIdx).slice(0, 5);
}
